import { Injectable, OnDestroy } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { LookupService } from '../services/lookup.service';
import { StudyViewModel } from './study.viewmodel';
import { LookupType } from '../mystudies/_study_constants';
import { ConfigService } from '@services/config.service';
import { forkJoin, Subject, Observable, firstValueFrom } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { RegionCountry } from '@models/regioncountry.model';
import { List } from 'linqts-es-module';
import { Study } from '@models/study.model';

const PeriodOfStudyFrom = 1990;
@Injectable()
export class StudyViewModelFactory implements OnDestroy {
  newStudy: StudyViewModel;
  newStudy$: Observable<StudyViewModel>;
  private destroy$ = new Subject();

  constructor(private lookupService: LookupService, private configService: ConfigService) {}

  public initializeViewModel(): Observable<StudyViewModel> {
    this.newStudy = new StudyViewModel();
    const fiscalYears$ = this.lookupService.getFiscalYears$();
    const financialResultsFormatting$ = this.lookupService.GetLookupValueList$(LookupType.FinancialResultsFormatting);
    const studySettings$ = this.lookupService.GetLookupValueList$(LookupType.StudySettings);
    const countriesProjectOffice$ = this.lookupService.GetProjectOfficeCountries$();
    const currencies$ = this.lookupService.GetCurrencies$();
    const currencyUnits$ = this.lookupService.GetCurrencyUnits$();
    const regionCountries$ = this.lookupService.GetAllRegionCountries$();
    const industries$ = this.lookupService.GetIndustries$();
    const industryTypes$ = this.lookupService.GetIndustryTypes$();
    const functionTypes$ = this.lookupService.GetFunctionTypes$();
    const roles$ = this.lookupService.GetAllRole$();

    this.newStudy.monthsBack = this.lookupService.getSequentialNumberList(1, 12);

    const part1$ = forkJoin([fiscalYears$, financialResultsFormatting$, studySettings$, countriesProjectOffice$, currencies$]).pipe(
      takeUntil(this.destroy$),
      tap(([_fiscalYears, _financialResultsFormatting, _studySettings, _countriesProjectOffice, _currencies]) => {
        this.newStudy.years = this.lookupService.getStudyYearList(PeriodOfStudyFrom);
        this.newStudy.fiscalYears = _fiscalYears;
        this.newStudy.financialResultsFormatting = _financialResultsFormatting;
        this.newStudy.studySettings = _studySettings;
        this.newStudy.countriesProjectOffice = _countriesProjectOffice;
        this.newStudy.currencies = _currencies;
      })
    );

    const ratios$ = this.lookupService.getAllFinancialRatio$();
    const defaultAveragingMethods$ = this.lookupService.GetAllAveragingMethods$();
    const isWorldRegionsEnabledOnTestedParty$ = this.configService.getConfigValueBoolean(ConfigService.IS_WORLD_REGIONS_ENABLED_ON_TESTED_PARTY);

    const part2$ = forkJoin([industries$, industryTypes$, functionTypes$, roles$]).pipe(
      takeUntil(this.destroy$),
      tap(([_industries, _industryTypes, _functionTypes, _roles]) => {
        this.newStudy.industries = _industries;
        this.newStudy.industryTypes = _industryTypes;
        this.newStudy.functionTypes = _functionTypes;
        this.newStudy.roles = _roles;
      })
    );

    const part3$ = forkJoin([currencyUnits$, regionCountries$, ratios$, defaultAveragingMethods$, isWorldRegionsEnabledOnTestedParty$]).pipe(
      takeUntil(this.destroy$),
      tap(([_currencyUnits, _regionCountries, _ratios, _defaultAveragingMethods, _isWorldRegionsEnabledOnTestedParty]) => {
        this.newStudy.currencyUnits = _currencyUnits;
        this.newStudy.regionCountryList = new List<RegionCountry>();
        this.newStudy.regionCountryList.AddRange(_regionCountries);

        this.newStudy.isWorldRegionsEnabledOnTestedParty = _isWorldRegionsEnabledOnTestedParty;
        this.newStudy.ratios = _ratios.filter(function (value) {
          return value.isPLIRatio === true;
        });
        this.newStudy.defaultAveragingMethods = _defaultAveragingMethods;
        if (!this.newStudy.study.currencyUnitId) {
          this.newStudy.study.currencyUnitId = this.newStudy.currencyUnits.find((c) => c.name.toLowerCase() === 'none')?.lookupId;
        }
        this.newStudy.regionCountries_multi = this.getMultiSelectRegionCountry();
        this.newStudy.testedpartyregionCountries_multi = this.getMultiSelectRegionCountry();
        this.newStudy.otherIndustryId = this.newStudy.industries.find((a) => a.industryName === 'Other').industryId;
        this.newStudy.study.otherIndustryId = this.newStudy.otherIndustryId; // need to pass this to server API for validation along with study object of Other Industry
      })
    );

    return forkJoin([part1$, part2$, part3$]).pipe(map(() => this.newStudy));
  }

  async initializeViewModelForEdit(studyVM: StudyViewModel): Promise<StudyViewModel> {
    this.newStudy = studyVM;
    this.newStudy = await this.getAllFieldsPopulated();

    if (this.newStudy.study.projectOfficeCountryId) {
      this.lookupService
        .GetCitiesByCountry(this.newStudy.study.projectOfficeCountryId)
        .then((countryCities) => (this.newStudy.countryCities = countryCities));
    }
    return Promise.resolve(this.newStudy);
  }

  private async getAllFieldsPopulated(): Promise<StudyViewModel> {
    this.newStudy.years = this.lookupService.getStudyYearList(PeriodOfStudyFrom);
    this.newStudy.fiscalYears = this.lookupService.getFiscalYears();

    this.newStudy.financialResultsFormatting = await this.lookupService.GetLookupValues(LookupType.FinancialResultsFormatting);
    this.newStudy.studySettings = await this.lookupService.GetLookupValues(LookupType.StudySettings);

    this.newStudy.countriesProjectOffice = await this.lookupService.GetProjectOfficeCountries();
    this.newStudy.currencies = await this.lookupService.GetCurrencies();
    this.newStudy.currencyUnits = await this.lookupService.GetCurrencyUnits();
    this.newStudy.regionCountryList = new List<RegionCountry>();
    this.newStudy.regionCountryList.AddRange(await this.lookupService.GetAllRegionCountries());
    this.newStudy.industries = await this.lookupService.GetIndustries();
    this.newStudy.industryTypes = await this.lookupService.GetIndustryTypes();
    this.newStudy.functionTypes = await this.lookupService.GetFunctionTypes();
    this.newStudy.roles = await this.lookupService.GetAllRole();
    this.newStudy.ratios = await this.lookupService.GetAllFinancialRatio();
    this.newStudy.monthsBack = this.lookupService.getSequentialNumberList(1, 12);
    this.newStudy.ratios = this.newStudy.ratios.filter(function (value) {
      return value.isPLIRatio === true;
    });
    this.newStudy.defaultAveragingMethods = await this.lookupService.GetAllAveragingMethods();
    if (!this.newStudy.study.currencyUnitId) {
      this.newStudy.study.currencyUnitId = this.newStudy.currencyUnits.find((c) => c.name.toLowerCase() === 'none').lookupId;
    }
    this.newStudy.regionCountries_multi = this.getMultiSelectRegionCountry();
    this.newStudy.testedpartyregionCountries_multi = this.getMultiSelectRegionCountry();
    this.newStudy.otherIndustryId = this.newStudy.industries.find((a) => a.industryName === 'Other').industryId;
    this.newStudy.study.otherIndustryId = this.newStudy.otherIndustryId; // need to pass this to server API for validation along with study object of Other Industry
    this.newStudy.isWorldRegionsEnabledOnTestedParty = await firstValueFrom(
      this.configService.getConfigValueBoolean(ConfigService.IS_WORLD_REGIONS_ENABLED_ON_TESTED_PARTY)
    );
    return Promise.resolve(this.newStudy);
  }

  private getMultiSelectRegionCountry() {
    const regionDetails = this.newStudy.regionCountryList
      .Select((a) => a.region)
      .Distinct()
      .OrderBy((a) => a.regionName.toUpperCase());

    // display Global region first
    const globalRegion = regionDetails.Where((r) => r.regionName == 'Global').FirstOrDefault();
    regionDetails.Remove(globalRegion);
    regionDetails.Insert(0, globalRegion);

    return regionDetails
      .GroupJoin(
        this.newStudy.regionCountryList,
        (ctry) => ctry.regionId,
        (rg) => rg.regionId,
        (ctry, rg) => ({
          label: ctry.regionName,
          data: '0',
          leaf: false,
          children: rg.Select((a) => ({ label: a.country.countryName, data: a.countryId.toString(), leaf: true })).ToArray(),
        })
      )
      .ToArray() as TreeNode[];
  }

  updateCurrencyInfoForStudy(study: Study) {
    if (study.currencyId !== study.currency.currencyId) {
      const currencyLookup = this.newStudy.currencies.find((c) => c.currencyId === study.currencyId);

      if (currencyLookup) {
        study.currency = currencyLookup;
      }
    }

    if (study.currencyUnitId !== study.currencyUnit.lookupId) {
      const unitLookup = this.newStudy.currencyUnits.find((c) => c.lookupId === study.currencyUnitId);

      if (unitLookup) {
        study.currencyUnit = unitLookup;
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
